import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { buildListParams, type DataGridQuery, type PageRequest } from "@/hooks/use-data-grid";
import { ptoBalanceKeys, ptoEntryCalendarEventKeys, ptoEntryKeys } from "@/lib/query";

export interface PtoEntry {
  id: string;
  startDate: string;
  endDate: string;
  totalHours: number | null;
  type: string;
  status: string;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  // EPIC-009: reverse visibility — the title of the suggestion currently
  // attached to this entry, if any (joined server-side, see queryPtoEntries).
  suggestionTitle: string | null;
}

interface PtoEntriesListResponse {
  data: PtoEntry[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

export interface PtoEntryInput {
  startDate: string;
  endDate: string;
  totalHours?: number | null;
  type: string;
  status?: string;
  note?: string | null;
}

export function usePtoEntries(query: DataGridQuery, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...ptoEntryKeys.list(query), pageRequest] as const,
    queryFn: async (): Promise<PtoEntriesListResponse> => {
      const res = await fetch(`/api/pto-entries?${buildListParams(query, pageRequest)}`);
      if (!res.ok) throw new Error("Failed to fetch PTO entries");
      return res.json();
    },
  });
}

export function usePtoEntry(entryId: string) {
  return useQuery({
    queryKey: ptoEntryKeys.detail(entryId),
    queryFn: async (): Promise<PtoEntry> => {
      const res = await fetch(`/api/pto-entries/${entryId}`);
      if (!res.ok) throw new Error("Failed to fetch PTO entry");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreatePtoEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: PtoEntryInput): Promise<PtoEntry> => {
      const res = await fetch("/api/pto-entries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to create PTO entry");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ptoEntryKeys.all });
      queryClient.invalidateQueries({ queryKey: ptoBalanceKeys.all });
      toast.success("PTO entry created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create PTO entry");
    },
  });
}

export function useUpdatePtoEntry(entryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<PtoEntryInput>) => {
      const res = await fetch(`/api/pto-entries/${entryId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Update failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ptoEntryKeys.all });
      queryClient.invalidateQueries({ queryKey: ptoBalanceKeys.all });
      toast.success("PTO entry updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update PTO entry");
    },
  });
}

export interface LastCalendarEventSend {
  at: string;
  recipients: string[];
}

/**
 * Most recent prior calendar-invite send for this entry, if any — powers the
 * send dialog's non-blocking "already sent" banner (EPIC-013). Only fetched
 * while the dialog is open (see .claude/rules/frontend.md's Dialogs rule).
 */
export function usePtoEntryCalendarEventStatus(entryId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ptoEntryCalendarEventKeys.lastSent(entryId),
    queryFn: async (): Promise<LastCalendarEventSend | null> => {
      const res = await fetch(`/api/pto-entries/${entryId}/calendar-event`);
      if (!res.ok) throw new Error("Failed to load calendar invite status");
      const json = await res.json();
      return json.data.lastSent;
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Sends a real Google Calendar invite (EPIC-013) covering this entry's date
 * range to `recipients`. No entry-cache invalidation — nothing on the entry
 * itself changes; only the "last sent" status is invalidated so reopening
 * the dialog shows the new send in its warning banner.
 */
export function useSendPtoEntryCalendarEvent(entryId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recipients: string[]) => {
      const res = await fetch(`/api/pto-entries/${entryId}/calendar-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipients }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Failed to send calendar invite");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ptoEntryCalendarEventKeys.lastSent(entryId) });
      toast.success("Calendar invite sent");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send calendar invite");
    },
  });
}

export function useDeletePtoEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await fetch(`/api/pto-entries/${entryId}`, { method: "DELETE" });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error ?? "Delete failed");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ptoEntryKeys.all });
      queryClient.invalidateQueries({ queryKey: ptoBalanceKeys.all });
      toast.success("PTO entry deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete PTO entry");
    },
  });
}
